<template>
<!-- TODO: upgrade to vue-google-charts v1.1.0 -->
<!-- HISTORY:
   V260218.1: Added an "Ignore Date Range" switch and fixed bugs where the date range was not added to the whereClause or could be added multiple times.
   V260105.1: Consumed the has-filter prop in bt-metrics-model and removed it from bt-dims-metrics-model, bt-dates-model, and bt-histogram-metrics-model.
   V251204.1: Added a new v-data-table implementation for the rows model to support server-side pagination.
   V250903.1: In the Result panel, replaced v-show with v-if because collapsing the panel caused all three <v-expansion-panel-content> sections to appear.
   V250811.1: Consumed metrics exclude prop to decide how to inject date range in the where clauses. BUSINESS LOGIC:
      If exclude is true (for *-metrics only), date range is injected in the metric filters instead of the global where clause.
   V250808.2: Modified orderedQueryKeys for dims model to parse fields with 'as' alias.
   V250807.2: Consumed has-filter prop of the models ending with '-metrics' + Added Export Data to CSV btn.
   V250805.2: Fixed a bug that didn't sort resultQuery properly. Modified table override logic.
   V250804.1: Removed one year restriction of date range + Increased dp-max-range from 365 to 1095 (3 years) + Sorted queryResult based on: dims/groupByDims/dateParts/metric labels/pivotDimIn +
      Built lake.year format based on the new approach (year/month) to support all the possible cases + Only used year/month values that already have parquet files.
   V250721.1: Added KPI option for the metrics model
   V250718.1: Added AreaChart and GeoChart: model startsWith 'dims', metrics & pivotDimIn = 1, certain dims.
   V250716.2: Added LineChart, BarChart, ColumnChart, PieChart and DonutChart options.
   V250710.1: Added histogram-metrics model.
   V250709.1: Added dims-dim model.
   V250708.1: Added date-dim model.
   V250707.1: Added rows model.
   V250703.1: For date-date model: prepopulated metrics by using initialMetrics prop + Moved date part from end to front due to the changes in the query result.
   V250702.1: Added date-metrics and date-date models (handled by using BtDatesModel).
   V250701.1: Added dims-metrics model.
   V250627.2: Converted date range to UTC and obtained schemas only when needed + Showed Model JSON + Wrapped different sections with panels + Fixed SUBMIT btn disabled/enabled.
   V250625.1: 1st release.
-->
<v-container fluid class="px-3 py-3">
   <v-card class="pb-2">
      <v-card-title>
         <h1 class="title font-weight-bold grey--text darken-4 pl-2" style="color:#757575 !important">
            <v-icon class="pr-1">query_stats</v-icon>
            <span>Query</span>
         </h1>
      </v-card-title>

      <v-card-text class="pb-2">
         <v-expansion-panels focusable
            v-model="panelQuery"
         >
            <v-expansion-panel>
               <v-expansion-panel-header>
                  <strong>Model Builder</strong>
               </v-expansion-panel-header>
               <v-expansion-panel-content :class="`pt-4 ${model ? '' : 'pb-2'}`">
                  <v-card flat>
                     <v-form lazy-validation
                        ref="mainForm"
                        v-model="isMainFormValid"
                     >
                        <v-row v-if="lakeTree?.length">
                           <v-col xs="12" md="3" class="py-2">
                              <!-- :dp-max-range="365" -->
                              <bt-date-picker
                                 :debug="debug"
                                 :dd-custom-label="true"
                                 dd-label="select a date range..."
                                 :dd-error-message="dateRangeErrMsg"
                                 :dd-monthly-items="datePickerMonthlyItems"
                                 :dp-no-title="false"
                                 :dp-max-range="datePickerMaxRange"
                                 v-model="dateRange"
                                 @date-range-change="dateRangeChanged"
                              ></bt-date-picker>
                           </v-col>
                           <v-col v-if="dateRange.length && schemas.length" xs="12" md="3" class="py-2">
                              <v-select dense persistent-hint hide-selected required
                                 ref="model"
                                 placeholder="select a model..."
                                 hint="* Model"
                                 :items="modelItems"
                                 v-model="model"
                                 @change="modelChanged"
                              ></v-select>
                           </v-col>
                           <!-- V260218 -->
                           <v-col xs="12" md="3" class="py-1" v-if="model">
                              <v-switch dense hide-details
                                 class="mt-4 mb-0"
                                 label="Ignore Date Range"
                                 v-model="ignoreDateRange"
                                 @change="ignoreDateRangeChanged"
                              ></v-switch>
                           </v-col>
                           <v-spacer></v-spacer>
                           <v-col v-if="dateRange.length" xs="12" md="3" class="d-flex justify-end align-center">
                              <v-btn small
                                 color="gray darken-1"
                                 :disabled="loadingSchemas || !schemas.length"
                                 :loading="loadingSchemas"
                                 @click="viewSchemaClicked"
                              >View Schema
                                 <v-icon right dark>schema</v-icon>
                              </v-btn>
                           </v-col>
                        </v-row>
                        <v-row v-if="schemas.length && model">
                           <v-col><v-divider></v-divider></v-col>
                        </v-row>
                        <v-row v-if="schemas?.length && model">
                           <v-col cols="12">
                              <bt-metrics-model v-if="model === 'metrics'"
                                 has-filter
                                 :initial-model="initialModel"
                                 :schemas="schemas"
                                 :query-submitted="isSubmitDisabled"
                                 v-model="modelData"
                                 @form-valid="modelDataChanged"
                              />
                              <!--V260105: has-filter -->
                              <bt-dims-metrics-model v-else-if="model === 'dims-metrics'"
                                 :schemas="schemas"
                                 :query-submitted="isSubmitDisabled"
                                 v-model="modelData"
                                 @form-valid="modelDataChanged"
                              />
                              <bt-dims-dim-model v-else-if="model === 'dims-dim'"
                                 :schemas="schemas"
                                 :query-submitted="isSubmitDisabled"
                                 :pivot-dim-in-items="pivotDimInItems"
                                 v-model="modelData"
                                 @form-valid="modelDataChanged"
                                 @pivot-dim-change="pivotDimChanged"
                              />
                              <!--V260105: has-filter -->
                              <bt-dates-model v-else-if="model.startsWith('date-')"
                                 :type="model"
                                 :schemas="schemas"
                                 :query-submitted="isSubmitDisabled"
                                 :pivot-dim-in-items="pivotDimInItems"
                                 v-model="modelData"
                                 @form-valid="modelDataChanged"
                                 @pivot-dim-change="pivotDimChanged"
                              />
                              <!--V260105: has-filter -->
                              <bt-histogram-metrics-model v-else-if="model === 'histogram-metrics'"
                                 :schemas="schemas"
                                 :query-submitted="isSubmitDisabled"
                                 v-model="modelData"
                                 @form-valid="modelDataChanged"
                              />
                              <bt-rows-model v-else-if="model === 'rows'"
                                 :schemas="schemas"
                                 :query-submitted="isSubmitDisabled"
                                 v-model="modelData"
                                 @form-valid="modelDataChanged"
                              />
                           </v-col>
                        </v-row>
                        <v-row v-if="schemasErrMsg" class="pt-4">
                           <v-col cols="12" class="pt-4">
                              <v-alert dense outlined
                                 type="error"
                              >{{schemasErrMsg}}</v-alert>
                           </v-col>
                        </v-row>
                     </v-form>
                     <v-card-actions v-if="schemas.length && model" class="px-0 py-0">
                        <div class="flex-grow-1"></div>
                        <v-btn text
                           color="blue darken-1"
                           :disabled="loadingQuery || isSubmitDisabled"
                           :loading="loadingQuery"
                           @click="submitQuery(true)"
                        >Submit Query</v-btn>
                     </v-card-actions>
                  </v-card>
               </v-expansion-panel-content>
            </v-expansion-panel>
         </v-expansion-panels>
      </v-card-text>

      <v-card-text v-if="!isEmpty(schemas) && !isEmpty(query)" class="pt-2">
         <v-expansion-panels focusable
            v-model="panelModel"
         >
            <v-expansion-panel>
               <v-expansion-panel-header>
                  <strong>Model</strong>
               </v-expansion-panel-header>
               <v-expansion-panel-content class="pt-5">
                  <v-row class="py-0">
                     <v-col cols="10" class="pt-1 pr-0 pb-4">
                        <pre v-if="isModelExpanded">{{JSON.stringify(this.query, null, 3)}}</pre>
                        <span v-else>{{JSON.stringify(this.query, null, 1)}}</span>
                     </v-col>
                     <v-col cols="2" class="px-0 py-0 d-flex justify-end">
                        <v-btn text xs-small
                           color="blue darken-1"
                           @click="isModelExpanded = !isModelExpanded"
                        >{{isModelExpanded ? 'Collapse' : 'Expand'}}
                           <v-icon v-if="isModelExpanded" right dark>chevron_right</v-icon>
                           <v-icon v-else right dark>expand_more</v-icon>
                        </v-btn>
                     </v-col>
                  </v-row>
               </v-expansion-panel-content>
            </v-expansion-panel>
         </v-expansion-panels>
      </v-card-text>

      <v-card-text v-if="!isEmpty(schemas) && !isEmpty(query)" class="pt-0">
         <v-expansion-panels focusable
            v-model="panelResult"
         >
            <v-expansion-panel>
               <v-expansion-panel-header>
                  <strong>Result</strong>
               </v-expansion-panel-header>
               <v-expansion-panel-content class="pt-2 pb-0">
                  <v-row class="pb-3">
                     <v-spacer></v-spacer>
                     <v-col cols="2" class="pt-5 pr-0">
                        <v-btn v-if="resultType === 'Table'"
                           small
                           class="mr-3"
                           @click="exportDataToCsvClicked"
                        >Export Data to CSV
                           <v-icon right dark>file_upload</v-icon>
                        </v-btn>
                     </v-col>
                     <v-col cols="2" class="pt-2 pb-0 pl-0">
                        <v-select dense persistent-hint hide-selected required
                           ref="resultType"
                           placeholder="select a result type..."
                           hint="* Result Type"
                           :disabled="resultTypeItems.length === 1"
                           :items="resultTypeItems"
                           v-model="resultType"
                           @change="resultTypeChanged"
                        ></v-select>
                     </v-col>
                  </v-row>
               </v-expansion-panel-content>
               <v-expansion-panel-content v-if="isTable" class="pt-0 pb-1">
                  <v-data-table dense fixed-header v-if="model === 'rows'"
                     class="elevation-1"
                     :footer-props="{
                        itemsPerPageOptions:[5, 10, 20],
                        showFirstLastPage: true
                     }"
                     :headers="queryResultHeaders"
                     :hide-default-footer="!queryCount"
                     :items="queryResult"
                     :items-per-page="5"
                     :loading="loadingTable"
                     :loading-text="$t('loading-text')"
                     :no-data-text="$t('no-data-text', { value: 'data' })"
                     :no-results-text="$t('no-results-text', { value: 'data' })"
                     :options.sync="tableOptions"
                     :server-items-length="queryCount"
                  />
                  <v-data-table dense fixed-header v-else
                     class="elevation-1"
                     :footer-props="{
                        itemsPerPageOptions: [5, 10, 20, -1],
                        showFirstLastPage: true
                     }"
                     :headers="queryResultHeaders"
                     :hide-default-footer="queryResult.length <= 5"
                     :items="queryResult"
                     :items-per-page="10"
                     :loading-text="$t('loading-text')"
                     :no-data-text="$t('no-data-text', { value: 'results' })"
                     :no-results-text="$t('no-results-text', { value: 'results' })"
                  >
                     <!--V250805.2: <template v-slot:item="props">
                        <tr>
                           <td v-for="(value, key) in props.item" :key="key">
                              {{ formatNumber(value, key) }}
                           </td>
                        </tr>
                     </template> -->
                     <template v-slot:item="{item}">
                        <tr>
                           <td v-for="header in queryResultHeaders" :key="header.value">
                              {{ formatNumber(item[header.value], header.value) }}
                           </td>
                        </tr>
                     </template>
                  </v-data-table>
               </v-expansion-panel-content>
               <v-expansion-panel-content v-if="isChart" class="pt-0 pb-1">
                  <!-- :events="chartEvents" resultType-->
                  <GChart
                     ref="gChart"
                     :data="chartData"
                     :type="chartType"
                     :options="chartOptions"
                     :settings="gChartSettings"
                     :key="resultType"
                  />
                  <v-row v-show="false">
                     <v-spacer></v-spacer>
                     <v-col cols="2" class="pl-0 pr-3 pt-0 pb-2 d-flex justify-end">
                        <v-switch dense hide-details
                           class="mb-0"
                           label="World Map"
                           v-model="isRegionChecked"
                        ></v-switch>
                     </v-col>
                  </v-row>
               </v-expansion-panel-content>
               <v-expansion-panel-content v-if="isKpi" class="pt-3 pb-1">
                  <v-card outlined class="pa-3">
                     <v-row dense
                        v-for="(value, key) in queryResult[0]"
                        :key="key"
                        class="body-1 font-weight-bold"
                     >
                        <v-col cols="12"><span class="grey--text text-darken-1">{{ key }}: </span>{{ formatNumber(value) }}</v-col>
                     </v-row>
                  </v-card>
                 </v-expansion-panel-content>
            </v-expansion-panel>
         </v-expansion-panels>
      </v-card-text>
   </v-card>

   <v-dialog no-click-animation persistent
      max-width="640px"
      v-model="dialogSchemas"
   >
      <v-card flat>
         <v-card-title class="title grey--text darken-4 font-weight-bold pb-2">Schemas:</v-card-title>

         <v-card-text class="pt-2 pb-0">
            <v-data-table dense fixed-header
               class="elevation-1"
               item-key="column_name"
               :footer-props="{
                  itemsPerPageOptions: [5, 10, 20, -1],
                  showFirstLastPage: true
               }"
               :headers="schemaHeaders"
               :hide-default-footer="schemas.length <= 5"
               :items="schemas"
               :items-per-page="10"
               :loading-text="$t('loading-text')"
               :no-data-text="$t('no-data-text', { value: 'schemas' })"
               :no-results-text="$t('no-results-text', { value: 'schemas' })"
            ></v-data-table>
         </v-card-text>

         <v-card-actions class="pr-4">
            <div class="flex-grow-1"></div>
            <v-btn text
               color="blue darken-1"
               @click="dialogSchemas = false"
            >Close</v-btn>
         </v-card-actions>
      </v-card>
   </v-dialog>
</v-container>
</template>

<script>
import BtDatePicker from "./BtDatePicker.vue";
import BtMetricsModel from './BtMetricsModel.vue';
import BtDimsMetricsModel from './BtDimsMetricsModel.vue';
import BtDimsDimModel from './BtDimsDimModel.vue';
import BtDatesModel from './BtDatesModel.vue';
import BtHistogramMetricsModel from './BtHistogramMetricsModel.vue';
import BtRowsModel from './BtRowsModel.vue';
import { GChart } from 'vue-google-charts';
import { APIService } from '../services/ls-api-service.js';
import { deepClone, isLocal, isEmpty, formatNumber, getUTCFromDate, getUTCToDate, exportDataToCSV } from '../mixins/bt-mixin.js';
// import { sleep } from '../mixins/bt-mixin.js';

const NAME = "LsQuery";
const MODELS = {
   metrics: 'metrics',
   dims: 'dims-metrics',
   dimsDim: 'dims-dim',
   date: 'date-metrics',
   dateDate: 'date-date',
   dateDim: 'date-dim',
   histogram: 'histogram-metrics',
   rows: 'rows'
}
const GEO_HIERARCHY = [
   ["country", "countries"],
   ["state", "states", "province", "provinces"],
   ["city", "cities", "town", "towns"]
];

function _isValidGeoHierarchy(geoFields) {
   const normalizedGeoFields = geoFields.map(f => f.toLowerCase());

  // Map each geo field to its hierarchy level
   const levels = normalizedGeoFields.map(field => {
      for (let i = 0; i < GEO_HIERARCHY.length; i++) {
         if (GEO_HIERARCHY[i].some(h => h === field)) {
            return i;
         }
      }

      return -1; // invalid field
   });

   // Check if all fields are recognized
   if (levels.includes(-1)) return false;

   // Check if levels are in ascending order (i.e., proper hierarchy)
   for (let i = 1; i < levels.length; i++) {
      if (levels[i] < levels[i - 1]) return false;
   }

   return true;
}

export default {
   name: NAME,

   components: {
      BtDatePicker,
      BtMetricsModel,
      BtDimsMetricsModel,
      BtDimsDimModel,
      BtDatesModel,
      BtHistogramMetricsModel,
      BtRowsModel,
      GChart
   },

   props: {
      debug: {
         type: Boolean,
         default: false
      },

      isActualEndpoint: {
         type: Boolean,
         default: true
      }
   },

   data() {
      return {
         datePickerMonthlyItems: [
            { text: "Current Month", value: "TM" },
            { text: "Current Quarter", value: "TQ" },
            { text: "Current Year", value: "TY" },
            { text: "Last Month", value: "LM" },
            { text: "Last Quarter", value: "LQ" },
            { text: "Last Year", value: "LY" }
         ],
         modelItems: [
            { text: 'Metrics', value: 'metrics' },
            { text: 'Dimensions - Metrics', value: 'dims-metrics' },
            { text: 'Dimensions - Dimension', value: 'dims-dim' },
            { text: 'Date - Metrics', value: 'date-metrics' },
            { text: 'Date - Date', value: 'date-date' },
            { text: 'Date - Dimension', value: 'date-dim' },
            { text: 'Histogram - Metrics', value: 'histogram-metrics' },
            { text: 'Rows', value: 'rows' }
         ],
         model: '',
         isMainFormValid: false,
         apiService: null,
         panelQuery: 0,
         panelModel: 0,
         panelResult: 0,
         dateRange: [],
         dateRangeErrMsg: '',
         loadingSchemas: false,
         dialogSchemas: false,
         prevLakeYear: '',
         //V260218: prevSchemas: '',
         schemas: [],
         schemaHeaders: [
            { text: 'Column Name', value: 'column_name', align: 'left', sortable: true },
            { text: 'Column Type', value: 'column_type', align: 'left', sortable: true }
         ],
         schemasErrMsg: '',
         lake: null,
         modelData: {},
         loadingQuery: false,
         query: null,
         queryResult: [],
         queryResultHeaders: [],
         isModelExpanded: false,
         fromDate: '',
         toDate: '',
         isSubmitDisabled: true,
         pivotDimInItems: [],
         eventDateWhereClause: '',
         chartData: [],
         gChartSettings: {
            packages: ['corechart', 'table', 'map', 'geochart'],
            mapsApiKey: process.env.VUE_APP_GOOGLE_API_KEY
         },
         isTable: true,
         isKpi: false,
         resultTypeItems: [],
         resultType: 'Table',
         chartType: 'LineChart',
         isRegionChecked: true,
         datePickerMaxRange: 1095,   //3*365
         lakeTree: [],
         parquetFilesSet: null,
         initialModel: null,
         loadingTable: false,
         tableOptions: {},
         queryCount: 0,
         isFirstTime: true,
         ignoreDateRange: false  //V260218
      }
   },

   computed: {
      token() {
         return this.$store.getters.token;
      },

      chartOptions() {
         const options = {
            height: "600", //400, 250
            legend: {
               position: "top"
            }
         };

         if (this.resultType === "DonutChart") {
            options.pieHole = "0.3";
         }

         if (this.resultType === "GeoChart") {
            options.displayMode = 'markers';    // this enables bubbles
            options.colorAxis = { colors: ['#666666','#000000'] };
            options.region = this.isRegionChecked ? 'world' : 'US';  // or 'US', 'AU', etc. depending on your focus
            // options.resolution = this.geoChartResolution;
         } else {
            options.chartArea = {
               left: "150", //70
               right: "50",   //20
               bottom: 100
            };
         }

         return options;

         // hAxis: {
         //    slantedText: true, // makes labels slanted (easier to read)
         //    slantedTextAngle: 45, // or 90 for vertical
         //    textStyle: {
         //       fontSize: 12,
         //       color: '#333',
         //    },
         //    maxAlternation: 1, // prevents skipping labels
         // },
         // chartArea: {
         //    left: 50,
         //    right: 20,
         //    bottom: 80, // more space at the bottom
         //    top: 20,
         // }
      },

      isChart() {
         return !this.isTable && !this.isKpi;
      },

      // isLocal() {
      //    return window.location.href.includes('http://localhost:8181/');
      // },

      initialMetrics() {
         if (!isLocal) return undefined;

         let modelData;
         switch (this.model) {
            case MODELS.metrics:
               modelData = {
                  metrics: [
                     { metric: 'count', on: '*', label: 'Count' },
                     { metric: 'sum', on: 'source_code', label: 'Total' },
                     // { metric: 'max', on: 'price', label: 'Maximum' },
                     // { metric: 'min', on: 'price', label: 'Minimum' }
                  ]
               };
               break;
            case MODELS.dims:
               modelData = {
                  dims: ['Countries','State','month(event_date) as event_date_month', "STRFTIME(CAST(event_date as DATE), '%Y-%m-%d') as event_date_date"],
                  // dims: ['Countries','State','month(event_date) as event_date_month', "STRFTIME(CAST(event_date as DATE), '%Y-%m-%d') as event_date_date"],
                  // orderBy: 'Countries asc,State asc,event_date_month asc',
                  orderBy: 'Countries asc,State asc,event_date_month asc,event_date_date asc',
                  whereClause: "where Countries = 'United States'"
               };
               break;
            case MODELS.dimsDim:
               modelData = {
                  groupByDims: ['Product'],
                  orderBy: 'Product asc',
                  // pivotDim: "Payment_Type",
                  // pivotDimIn: [ "Amex", "Mastercard" ],
                  pivotDim: "event_code",
                  pivotDimIn: [ "Sales", "70111", "70110" ],
                  pivotDimQuote: true
               };
               break;
            case MODELS.date:
            case MODELS.dateDate:
               modelData = {
                  dateField: "Account_Created",
                  dateParts: [ "year", "quarter" ]
               };
               break;
            case MODELS.dateDim:
               modelData = {
                  dateField: "Account_Created",
                  dateParts: [ "year", "quarter" ],
                  pivotDim: "Product",
                  pivotDimIn: [ "Product1", "Product2" ],
                  pivotDimQuote: true
               };
               break;
            case MODELS.histogram:
               modelData = {
                  histogramOn: "price",
                  histogramRange: [ 1000, 10000 ],
                  histogramStep: 1000,
                  histogramLable: "PriceRange"
               };
               break;
            case MODELS.rows:
               return {
                  columns: [ "Product", "price", "Payment_Type", "State", "app_code", "event_code", "Transaction_date"]
               };
            default:
               modelData = {};
               break;
         }

         modelData.metrics = modelData.metrics || [{
            metric: 'count',
            on: '*',
            label: 'Count'
         }];

         return modelData;
      }
   },

   watch: {
      token() {
         this.init();
      },

      tableOptions: {
         immediate: false,
         handler () {
            if (!this.isFirstLoad) {
               this.submitQuery(false);
            }
         }
      }
   },

   methods: {
      isEmpty,

      log(msg) {
         if (this.debug)
            console.log(`-----${NAME} V260218.1 says => ${msg}`);
      },

      logout() {
         this.$router.push('/');
      },

      async init() {
         if (this.token) {
            this.apiService = new APIService(this.jwt, this.token, this.debug, this.isActualEndpoint);
            await this.getLakeDirectories();
            this.getParquetFiles();
            this.schemasErrMsg = this.parquetFilesSet?.size ? '' : "There is no data available. Please create one or more ETLs to upload some files.";
         }
      },

      async dateRangeChanged(val) {
         // alert(`in dateRangeChange(): val=${JSON.stringify(val)}`);
         this.loadingSchemas = true;
         this.dateRangeErrMsg = '';
         this.query = {};
         this.queryResult = [];
         this.chartData = [];
         //V260218: this.eventDateWhereClause = '';
         this.fromDate = getUTCFromDate(val[0]);
         this.toDate = getUTCToDate(val[1]);
         const fromParts = this.fromDate.split('-');
         const toParts = this.toDate.split('-');
         const fromYear = fromParts[0];
         const toYear = toParts[0];
         const fromMonth = fromParts[1].replace(/^0/, '');
         const toMonth = toParts[1].replace(/^0/, '');
         const lakeYear = [];

         for (let year = +fromYear; year <= +toYear; year++) {
            const startMonth = year === +fromYear ? +fromMonth : 1;
            const endMonth = year === +toYear ? +toMonth : 12;

            for (let month = startMonth; month <= endMonth; month++) {
               lakeYear.push(`${year}/${month}`);
            }
         }

         const parquetAvailability = this.splitParquetAvailability(lakeYear);
         const availableLakeYears = Array.from(parquetAvailability.withParquet);
         if (!availableLakeYears.length) {
            this.schemasErrMsg =  "There is no data available for the specified date range. Please adjust your date range or upload the missing data by creating new ETLs.";
            this.schemas = [];
            this.model = '';
            this.loadingSchemas = false;
            return;
         }

         this.schemasErrMsg = '';
         //V260218: Moved here
         this.eventDateWhereClause = `event_date between '${this.fromDate}' and '${this.toDate}'`;
         this.lake = {
            year: availableLakeYears.join(','),  //lakeYear.join(','),
            month: '|',
            fileFormat: 'parquet'
         };

         if (this.lake.year != this.prevLakeYear) {
            this.prevLakeYear = this.lake.year;

            if (this.lake.year) {
               let result = await this.apiService.getSchemas(this.lake);              
               if (result.logout)
                  this.logout();
               else if (result.message) {
                  this.schemas = [];
                  this.schemasErrMsg = result.message;
               } else {
                  //V260218: if (JSON.stringify(result.data) != this.prevSchemas) {
                  if (JSON.stringify(result.data) != JSON.stringify(this.schemas)) {
                     this.schemas = result.data;
                     // V260218
                     // this.prevSchemas = JSON.stringify(result.data);
                     // if (this.schemas.find(item => item.column_name.toLowerCase() === 'event_date' && item.column_type === 'TIMESTAMP')) {
                     //    this.eventDateWhereClause = `event_date between '${this.fromDate}' and '${this.toDate}'`;
                     // }
                     if (!this.schemas.find(item => item.column_name.toLowerCase() === 'event_date' && item.column_type === 'TIMESTAMP')) {
                        this.eventDateWhereClause = '';
                     }
                  } else {
                     this.isSubmitDisabled = false;
                  }
               }
            }
         } else {
            this.isSubmitDisabled = false;
         }

         this.loadingSchemas = false;

         // alert('in dateRangeChanged(): schemas=' + JSON.stringify(this.schemas))
      },

      modelChanged() {
         this.initialModel = undefined;
         this.query = {};
         this.queryResult = [];
         this.pivotDimInItems = [];
         this.chartData = [];
         this.isSubmitDisabled = false;   //V260218

         if (isLocal) {
            // setTimeout(() => {
               this.modelData = this.initialMetrics;
               this.initialModel = true;
               this.submitQuery(true);
               // this.initialModel = false;
            // }, 100);
         }
      },

      // V260218
      ignoreDateRangeChanged() {
         this.query = {};
         this.queryResult = [];
         this.chartData = [];
         this.isSubmitDisabled = false;
      },

      viewSchemaClicked() {
         this.dialogSchemas = true;
      },

      modelDataChanged(val) {
         this.isSubmitDisabled = !val;
      },

      async pivotDimChanged(pivotDim) {
         this.pivotDimInItems = [];
         const query = {
            type: 'column-distinct',
            column: pivotDim,
            //V260218: whereClause: this.eventDateWhereClause ? 'where ' + this.eventDateWhereClause : '',
            whereClause: (this.eventDateWhereClause && !this.ignoreDateRange) ? 'where ' + this.eventDateWhereClause : '',
            lake: this.lake
         };

         let result = await this.apiService.getQueryResult(query);
         if (result.logout)
            this.logout();
         else if (!result.message && result.data?.length) {
            this.pivotDimInItems = result.data.map(obj => Object.values(obj)[0]).sort();
         }

         // alert('pivotDimInItems=' + JSON.stringify(this.pivotDimInItems))
      },

      async submitQuery(getCounts) {
         if (getCounts)
            this.loadingQuery = true;
         else
            this.loadingTable = true;

         this.queryResult = [];
         this.queryResultHeaders = [];
         this.chartData = [];
         // V260218
         // const query = {
         //    type: this.model,
         //    ...this.modelData,
         //    lake: this.lake
         // };
         const query = {
            type: this.model,
            ...deepClone(this.modelData),
            lake: deepClone(this.lake)
         };

         if (this.eventDateWhereClause && !this.ignoreDateRange) {   //V260218: Added the 2nd condition
            const hasExclude = query.metrics?.some(item => item.exclude === true);
            if (hasExclude) {
               query.metrics.filter(m => !m.exclude).forEach(metric => {
                  if (metric.filter)
                     metric.filter += ' and ' + this.eventDateWhereClause;
                  else
                     metric.filter = 'where ' + this.eventDateWhereClause;
               });
               
               query.lake.year = '*';
               query.lake.month = '*';
            } else {
               if (query.whereClause)
                  query.whereClause += ' and ' + this.eventDateWhereClause;
               else
                  query.whereClause = 'where ' + this.eventDateWhereClause;
            }
         }

         if (['dims-dim', 'date-dim'].includes(this.model)) {
            const whereClause = `${query.pivotDim} in (${query.pivotDimIn.map(v => `'${v}'`).join(',')})`;
            if (query.whereClause)
               query.whereClause += ' and ' + whereClause;
            else
               query.whereClause = 'where ' + whereClause;
         }

         this.query = { ...query };

         if (this.model === MODELS.rows) {
            if (getCounts) {
               this.isFirstLoad = true;
               await this.getQueryCount();
               if (this.queryCount) {
                  this.tableOptions.page = 1;
               } else {
                  return;
               }
            }

            const { page, itemsPerPage } = this.tableOptions;
            const limit = itemsPerPage || 5;
            this.query.limit = limit;
            this.query.offset = page ? (page - 1) * limit : 0;
         }

         let result = await this.apiService.getQueryResult(this.query);
         // await sleep(10000)
         if (result.logout)
            this.logout();
         else if (!result.message && result.data?.length) {
            const labels = query.metrics?.map(m => m.label);
            let orderedQueryKeys;
            if (this.model === MODELS.dateDate) {
               this.queryResult = this.reorderDateDateResult(result.data);
               orderedQueryKeys = Object.keys(this.queryResult[0]);
            } else {
               this.queryResult = result.data;
               switch (this.model) {
                  case MODELS.metrics:
                     // this.queryResult = this.reorderQueryResult(result.data, labels);
                     orderedQueryKeys = labels;
                     break;
                  case MODELS.dims:
                     // this.queryResult = this.reorderQueryResult(result.data, [...query.dims, ...labels]);
                     //V250807.2: orderedQueryKeys = [...query.dims, ...labels];
                     orderedQueryKeys = [
                        //V250808.1: ...query.dims.map(d => d.includes(' as ') ? d.split(' as ')[1] : d),
                        ...this.getDims(),
                        ...labels
                     ];
                     break;
                  case MODELS.dimsDim:
                     // this.queryResult = this.reorderQueryResult(result.data, orderedQueryKeys);
                     orderedQueryKeys = [...query.groupByDims, ...query.pivotDimIn];
                     break;
                  case MODELS.date:
                     // this.queryResult = this.reorderQueryResult(result.data, [...query.dateParts, ...labels]);
                     orderedQueryKeys = [...query.dateParts, ...labels];
                     break;
                  // case MODELS.dateDate:
                  //    this.queryResult = this.reorderDateDateResult(result.data);
                  //    orderedQueryKeys = Object.keys(this.queryResult[0]);
                  //    break;
                  case MODELS.dateDim:
                     // this.queryResult = this.reorderQueryResult(result.data, [...query.dateParts, ...query.pivotDimIn]);
                     orderedQueryKeys = [...query.dateParts, ...query.pivotDimIn];
                     break;
                  default:
                     orderedQueryKeys = Object.keys(result.data[0]);
                     break;
               }
            }
            
            // const keys = Object.keys(this.queryResult[0]);
            this.queryResultHeaders = orderedQueryKeys.map(key => ({
               text: key,  //this.model === 'date-date' ? toPascalCase(key) : key
               value: key,
               align: 'left',
               sortable: this.model !== MODELS.rows
            }));

            this.getResultTypeItems();
            this.resultTypeChanged();
            this.isFirstLoad = false;
         }

         this.log('queryResultHeaders=' + JSON.stringify(this.queryResultHeaders))

         this.loadingQuery = false;
         this.loadingTable = false;
         this.isSubmitDisabled = true;
      },

      // V250703
      reorderDateDateResult(data) {
         // const prefix = toPascalCase(this.modelData.dateParts[1]) + ' ';
         const prefix = this.modelData.dateParts[1] + ' ';
         const renamedData = data.map(obj => {
            const newObj = {};
            Object.entries(obj).forEach(([key, value]) => {
               if (!isNaN(key)) {
                  newObj[`${prefix}${key}`] = value;
               } else {
                  newObj[key] = value;
               }
            });

            return newObj;
         });

         const result = [];
         const keys = Object.keys(renamedData[0]);
         const lastKey = keys[keys.length - 1];
         JSON.parse(JSON.stringify(renamedData)).forEach(item => {
            const orderedMap = new Map();
            orderedMap.set(lastKey, item[lastKey]);
            for (let i = 0; i < keys.length - 1; i++) {
               orderedMap.set(keys[i], item[keys[i]]);
            }
            result.push(Object.fromEntries(orderedMap));
         });

         // console.warn('in reorderDateDateResult(): result=' + JSON.stringify(result));
         return JSON.parse(JSON.stringify(result));
      },

      // V250804
      // V250805: not in use
      reorderQueryResult(data, fields) {
         return data.map(obj => {
            const reordered = {};
            fields.forEach(field => {
               if (field in obj) {
                  reordered[field] = obj[field];
               }
            });
            // Optionally add remaining fields not in fieldOrder
            // Object.keys(obj).forEach(key => {
            //    if (!(key in reordered)) {
            //       reordered[key] = obj[key];
            //    }
            // });
            return reordered;
         });
      },

      formatNumber(val, key) {
         if (typeof val === 'string' || ['year', 'quarter', 'month', 'day', 'hour'].includes(key))
            return val;
         else
            return formatNumber(val, 2);
      },

      getResultTypeItems() {
         this.resultTypeItems = [
            { text: 'Table', value: 'Table' },
         ];

         if (this.model === 'metrics') {
            this.resultTypeItems.push ({ text: 'KPI', value: 'KPI' });
         } else if (this.model != MODELS.rows) {
            this.resultTypeItems.push (...[
               { text: 'Area Chart', value: 'AreaChart' },
               { text: 'Bar Chart', value: 'BarChart' },
               { text: 'Column Chart', value: 'ColumnChart' },
               { text: 'Line Chart', value: 'LineChart' }
            ]);

            if (this.query.metrics?.length === 1) {
               if (
                  this.model.endsWith('-metrics') ||
                  (this.model.endsWith('-dim') && this.query.pivotDimIn.length === 1)
               ) {
                  this.resultTypeItems.push (...[
                     { text: 'Pie Chart', value: 'PieChart' },
                     { text: 'Donut Chart', value: 'DonutChart' }
                  ]);
               }

               if (this.model.startsWith('dims-')) {
                  const dimKeys = this.query.dims || this.query.groupByDims;
                  const metricKeys = this.query.pivotDimIn || this.query.metrics.map(m => m.label);
                  if (metricKeys.length === 1 && _isValidGeoHierarchy(dimKeys)) {
                     this.resultTypeItems.push (
                        { text: 'Geo Chart', value: 'GeoChart'}
                     );
                  }
               }
            }
         }

         const currResultType = this.resultTypeItems.find(item => item.value === this.resultType);

         if (!this.resultType || !currResultType) {
            this.resultType = this.resultTypeItems[0].value;
            this.isTable = true;
            this.isKpi = false;
         }

         // { text: 'Combo Chart', value: 'ComboChart' },
      },

      resultTypeChanged() {
         this.isTable = this.resultType === 'Table';
         this.isKpi =  this.resultType === 'KPI';
         if (this.isChart) {
            setTimeout(() => {
               this.chartType = this.resultType.replace('Donut', 'Pie');
            }, 5);

            if (!this.chartData.length && this.queryResult.length)
               this.prepareChartData();
         }
      },

      async prepareChartData() {
         let dimKeys, metricKeys;
         switch (this.model) {
            case MODELS.dims: //'dims-metrics':
               // dimKeys = this.query.dims;
               dimKeys = this.getDims();
               metricKeys = this.query.metrics.map(m => m.label);
               break;
            case MODELS.dimsDim: //'dims-dim':
               dimKeys = this.query.groupByDims;
               metricKeys = this.query.pivotDimIn;
               break;
            case MODELS.date: //'date-metrics':
               dimKeys = this.query.dateParts;
               metricKeys = this.query.metrics.map(m => m.label);
               break;
            case MODELS.dateDate:   //'date-date':
               this.chartData = this.prepareChartData4DateDate();
               return;
            case MODELS.dateDim: //'date-dim':
               dimKeys = this.query.dateParts;
               metricKeys = this.query.pivotDimIn;
               break;
            case MODELS.histogram:  //'histogram-metrics':
               dimKeys = [this.query.histogramLable];
               metricKeys = this.query.metrics.map(m => m.label);
               break;
            default:
               alert(`Error in prepareChartData(): Unsupported model: ${this.model}`);
               break;
         }

         if (this.resultType === 'GeoChart') {
            this.chartData = this.prepareGeoChartData(dimKeys, metricKeys[0]);
         } else {
         const header = ['Label', ...metricKeys];
            const rows = this.queryResult.map(item => {
               const label = dimKeys.map(key => item[key]).join('-');
               const metrics = metricKeys.map(key => item[key] ?? 0);
               return [label, ...metrics];
            });

            // console.warn('chartData=' + JSON.stringify([header, ...rows]));
            this.chartData = [header, ...rows];
         }
      },

      prepareChartData4DateDate() {
         const xKey = this.query.dateParts[0];
         const header = Object.keys(this.queryResult[0]);
         const seriesKeys = header.filter(key => key !== xKey);
         const rows = this.queryResult.map(item => {
            const xVal = String(item[xKey]);
            const values = seriesKeys.map(key => item[key] ?? 0);
            return [xVal, ...values];
         });

         return [header, ...rows];
      },

      prepareGeoChartData(dimKeys, metricKey) {
         let chartData = [["Location", metricKey]];
         for (const row of this.queryResult) {
            const location = dimKeys.map(field => row[field]).filter(Boolean).join("-");  // was /
            chartData.push([location, row[metricKey]]);
         }
         // // const locations = chartData.slice(1).map(row => row[0]);
         // // this.geoChartResolution = locations.every(loc => loc.includes('/')) ? 'provinces' : 'countries';
         // // console.warn('geoChartResolution=' + this.geoChartResolution);

         // console.warn('chartData=' + JSON.stringify(chartData));
         return chartData;
      },

      async getLakeDirectories() {
         let result = await this.apiService.getLakeDirectories();
         if (result.logout)
            this.logout();
         else if (result.message) {
            this.lakeTree = [];
         } else {
            this.lakeTree = result.data;
         }
      },

      getParquetFiles() {
         const available = new Set();

         // Traverse directory data
         for (const yearNode of this.lakeTree) {
            const year = yearNode.name;
            if (!yearNode.children) continue;

            for (const monthNode of yearNode.children) {
               const month = monthNode.name;
               const files = monthNode.children || [];
               const hasParquet = files.some(file => file.name.endsWith('.parquet'));
               if (hasParquet) {
                  available.add(`${year}/${+month}`); // normalize month like 7 instead of "07"
               }
            }
         }

         this.parquetFilesSet = available;
      },

      splitParquetAvailability(monthPaths) {
         const withParquet = [];
         const withoutParquet = [];

         for (const ym of monthPaths) {
            if (this.parquetFilesSet.has(ym)) {
               withParquet.push(ym);
            } else {
               withoutParquet.push(ym);
            }
         }

         return { withParquet, withoutParquet };
      },

      // V250807.2
      exportDataToCsvClicked() {
         exportDataToCSV(
            this.queryResultHeaders.map(h => h.text),
            this.queryResult,
            `${this.model}_${this.fromDate.split('T')[0]}_To_${this.toDate.split('T')[0]}.csv`
         );
      },

      getDims() {
         return this.query.dims.map(d => {
            const parts = d.split(' as ');
            return parts[parts.length - 1];
         });
      },

      async getQueryCount() {
         this.queryCount = 0;
         const query = {
            type: 'metrics',
            metrics:[{ "metric":"count", "on":"_id", "label":"Count" }],
            whereClause: this.query.whereClause,
            lake: this.query.lake
         };

         const result = await this.apiService.getQueryResult(query);
         if (result.logout)
            this.logout();
         else if (!result.message && result.data?.length)
            this.queryCount = result.data[0].Count;
      }
   },

   created() {
      this.init();
   }
}
</script>

<style scoped>
</style>